import { defineStore } from 'pinia';
import { getSupportedLanguages } from '@/api/common';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, STORAGE_KEYS } from '@sys/shared/constants';
import { isApiSuccess, cache } from '@sys/shared/utils';

const LOCALE_CACHE_KEY = `${STORAGE_KEYS.LOCALE}_list`;

export const useLocaleStore = defineStore('locale', {
  state: () => ({
    /** 启用的语言列表 [{ code, label }]，接口获取 */
    enabledLocales: [],
    /** 默认语言 code，接口获取 */
    defaultLocale: '',
    /** 是否已加载 */
    loaded: false,
    /** 是否正在请求中（防重入） */
    _fetching: false,
  }),

  getters: {
    /** 安全获取默认语言（接口未加载时降级为静态常量） */
    safeDefaultLocale(state) {
      return state.defaultLocale || DEFAULT_LOCALE;
    },
    /** 安全获取语言列表（接口未加载时降级为静态常量） */
    safeLocales(state) {
      return state.enabledLocales.length > 0 ? state.enabledLocales : SUPPORTED_LOCALES;
    },
    /** 根据 code 查找语言对象 */
    getLocaleByCode(state) {
      return code => {
        const list = state.enabledLocales.length > 0 ? state.enabledLocales : SUPPORTED_LOCALES;
        return list.find(l => l.code === code);
      };
    },
    /** 获取语言 label */
    getLocaleLabel() {
      return code => {
        const found = this.getLocaleByCode(code);
        return found ? found.label : code;
      };
    },
  },

  actions: {
    /** 由外部设置默认语言（登录接口返回的 lang 字段） */
    setDefaultLocale(lang) {
      if (lang) {
        this.defaultLocale = lang;
      }
    },

    /**
     * 初始化：优先读持久化缓存，无缓存才调接口
     * 登录页调用 refresh() 强制更新缓存
     */
    async init() {
      if (this.loaded) return;

      // 1. 尝试从缓存恢复
      const cached = cache.get(LOCALE_CACHE_KEY);
      if (cached && Array.isArray(cached) && cached.length > 0) {
        this.enabledLocales = cached;
        this.loaded = true;
        return;
      }

      // 2. 无缓存，调接口
      await this._fetchAndCache();
    },

    /** 强制刷新（登录页进入时调用） */
    async refresh() {
      cache.remove(LOCALE_CACHE_KEY);
      this.loaded = false;
      await this._fetchAndCache();
    },

    /** 内部：调接口 → 写缓存 */
    async _fetchAndCache() {
      if (this._fetching) return;
      this._fetching = true;
      try {
        const res = await getSupportedLanguages();
        if (!isApiSuccess(res) || !Array.isArray(res.data)) {
          console.warn('[localeStore] 接口返回异常，降级使用静态配置', res);
          return;
        }
        this.enabledLocales = res.data.map(item => ({
          code: item.code,
          label: item.name || item.code,
        }));

        // 如果还没被登录接口设置过默认语言，用接口的 is_default 字段
        if (!this.defaultLocale) {
          const defaultItem = res.data.find(item => item.is_default === 1);
          if (defaultItem) {
            this.defaultLocale = defaultItem.code;
          } else if (this.enabledLocales.length > 0) {
            // 没有任何 is_default，取列表第一条兜底
            this.defaultLocale = this.enabledLocales[0].code;
          }
        }

        cache.set(LOCALE_CACHE_KEY, this.enabledLocales);
        this.loaded = true;
      } catch (e) {
        console.warn('[localeStore] 获取支持语言列表失败，降级使用静态配置', e);
      } finally {
        this._fetching = false;
      }
    },
  },
});
